import { DateTime } from 'luxon';
import { Theme } from '@routific/hive';

export const CUSTOMER_PROFILE_BULK_DELETE_LIMIT = 500;

export enum GraphQLErrorCodes {
  OPTIMIZE_FAILED = 'OPTIMIZE_FAILED',
  USAGE_LIMIT_EXCEEDED = 'USAGE_LIMIT_EXCEEDED',
  OPTIMIZE_ROUTES_FAILED = 'OPTIMIZE_ROUTES_FAILED',
  SCHEDULE_STOPS_ON_DATE_FAILED = 'SCHEDULE_STOPS_ON_DATE_FAILED',
  OPTIMIZE_ORDERS_TO_MULTIPLE_TARGET_ROUTES_FAILED = 'OPTIMIZE_ORDERS_TO_MULTIPLE_TARGET_ROUTES_FAILED',
  DUPLICATE_ROUTE_FAILED = 'DUPLICATE_ROUTE_FAILED',
  LAST_ADMIN_CONSTRAINT = 'LAST_ADMIN_CONSTRAINT',
}

export enum DateFormatFamilyEnum {
  MM_FIRST = 'MM-first',
  DD_FIRST = 'DD-first',
  YYYY_FIRST = 'YYYY-first',
}

export const DATE_FORMAT_FAMILIES: Record<DateFormatFamilyEnum, string[]> = {
  [DateFormatFamilyEnum.MM_FIRST]: ['MMDDYYYY', 'MM-DD-YYYY', 'MM/DD/YYYY'],
  [DateFormatFamilyEnum.DD_FIRST]: ['DDMMYYYY', 'DD-MM-YYYY', 'DD/MM/YYYY'],
  [DateFormatFamilyEnum.YYYY_FIRST]: ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYYMMDD'],
};

export const defaultDateFormat = 'YYYY-MM-DD';
export const defaultDisplayDateFormat = 'MMM d, yyyy';
export const defaultDisplayDateTimeFormat = 'MMM d, yyyy, h:mma';
export const militaryDisplayDateTimeFormat = 'MMM d, yyyy, HH:mm';

// Formats a DateTime for display lowercase am/pm, respecting 12h/24h preference.
export function formatDisplayDateTime(dt: DateTime, dateTimeFormat?: string): string {
  if (dateTimeFormat === 'MILITARY') {
    return dt.toFormat(militaryDisplayDateTimeFormat);
  }
  return dt.toFormat(defaultDisplayDateTimeFormat).replace(/(AM|PM)/g, (m) => m.toLowerCase());
}

export const defaultDateTimeFormat = 'YYYY-MM-DDTHH:mm';
export const defaultLongDateFormat = 'MMMM DD, YYYY';
export const hrsMinFormat = 'HH:mm';
export const twelveHrsMinFormat = 'h:mma';
export const monthNameDayNumberFormat = 'MMM D';
export const defaultTimeFormat = 'H:mm'; // Military time 0 .. 23
export const localizedTimeFormat = 'LT'; // 3:51 PM/AM
export const hourTimeFormat = 'hA';
export const smallHourFormat = 'HH';
export const smallMinuteFormat = 'm';
export const nonMilitaryTimeFormat = 'hh:mma';
export const defaultTimeInterval = '00:30';
export const fullDayFormat = `${monthNameDayNumberFormat}, ${localizedTimeFormat}`;

export const defaultDayInterval = {
  start: '00:00',
  end: '23:30',
};

export const DUMMY_LOADING_ARRAY: number[] = Array.from(Array(10).keys());

export const VALID_DATE_FORMATS = Object.values(DATE_FORMAT_FAMILIES).flat();

export const VALID_TIME_FORMATS = [
  localizedTimeFormat,
  defaultTimeFormat,
  hrsMinFormat,
  hourTimeFormat,
];

export const VALID_TIME_INTERVAL_FORMATS = [
  hrsMinFormat,
  nonMilitaryTimeFormat,
];

export const TIME_REGEX = /^([0-1]?\d|2[0-3]):[0-5]\d(am|pm)?$/;

export const POLL_COUNT_LIMIT = 10; // poll for ~6.5m on large jobs, 2.5m on small jobs
export const POLL_FREQUENCY_LONG = 6000;

export const DEFAULT_LINE_ZINDEX = 1;
export const SELECTED_LINE_ZINDEX = 2;
export const HOVERED_LINE_ZINDEX = 2;
export const MULTI_ACTION_BAR_ZINDEX = 1000;

export const LINEWIDTH_UNIT = 'pixels';
export const DEFAULT_LINEWIDTH = 3;
export const HOVERED_LINEWIDTH = 4;
export const DEFAULT_LINEWIDTH_OUTLINE = 6;
export const HOVERED_LINEWIDTH_OUTLINE = 9;
export const MAX_OFFLINE_MINUTES = 5;
export const CHECK_LATENESS_FREQUENCY_MS = 60000;
export const DEFAULT_PROJECT_NAME = 'My First Project';
export const ONLINE_CHECK_FREQUENCY_MS = 60000;
export const ONE_HOUR_IN_MINUTES = 60;
export const EIGHT_HOURS = 8;

export const MARKER_ICON_TRANSITION_DURATION = 150;

export const SEARCH_DEBOUNCE_MS = 300;
export const ORDERS_FILTERED_ANALYTICS_DEBOUNCE_MS = 3000;
export const SEARCH_INPUT_MAX_WIDTH = 562;
export const ORDERS_SEARCH_MIN_LENGTH = 3;
export const ORDERS_SEARCH_MAX_LENGTH = 50;
export const ORDERS_DEFAULT_SEARCH_RANGE_DAYS = 30;
export const ORDERS_MAX_CREATED_DATE_RANGE_MONTHS = 3;

export enum SolutionActionType {
  OPTIMIZE_ASSIGN = 'optimize_assign',
  OPTIMIZE_PROJECT = 'optimize_project',
  ASSIGN_SEQUENCE = 'assign_sequence',
}

export enum SolutionActionStatus {
  COMPLETED = 'Completed',
  FAILED = 'Failed',
}

export enum RouteActionType {
  ROUTE_CREATED = 'route_created',
  ROUTE_UPDATED = 'route_updated',
  ROUTE_DELETED = 'route_deleted',
  STOP_MOVING = 'stop_moving',
  STOP_MOVED = 'stop_moved',
  STOP_MOVE_FAILED = 'stop_move_failed',
}

export const XXXXS_COL_WIDTH = 8;
export const XXXS_COL_WIDTH = 48;
export const XXS_COL_WIDTH = 64;
export const XS_COL_WIDTH = 80;
export const SM_COL_WIDTH = 100;
export const MD_COL_WIDTH = 170;
export const LG_COL_WIDTH = 240;
export const XL_COL_WIDTH = 300;
export const HIDDEN_COL_WIDTH = 1;

// Schedule Orders Table
export const ROUTE_NAME_COL_WIDTH = '200px';
export const STATUS_COL_WIDTH = '120px';
export const DRIVER_COL_WIDTH = '200px';
export const NUM_OF_STOPS_COL_WIDTH = '120px';
export const DISTANCE_COL_WIDTH = '120px';
export const TIME_COL_WIDTH = '120px';
export const CAP_COL_WIDTH = '120px';
export const TAGS_COL_WIDTH = '200px';
export const SELECTED_COL_WIDTH = '48px';

export const ROW_SELECTION_COL_WIDTH = 40;

export const PANEL_WIDTH = 400;
export const MAX_PANEL_WIDTH = 572;

export const DEFAULT_BOTTOM_PANEL_HEIGHT = 285;
export const BOTTOM_PANEL_TOP_CHROME_HEIGHT = 112;
// Taller than any screen; the DrawerLayout clamps the sheet to its live measured height.
export const BOTTOM_SHEET_FILL_HEIGHT = 10000;

export const EXPANDED_PANEL_PADDING = 150;

export const DEFAULT_GANTT_HEIGHT = 416;
export const TIMELINE_VIEW_MAP_PADDING = DEFAULT_GANTT_HEIGHT;
export const SCHEDULING_PANEL_BORDER_AND_PADDING = 2;
export const SCHEDULING_PANEL_WIDTH = PANEL_WIDTH;

export const TRANSITION_DELAY_MILLI = 150;

export const DELETE_ROUTE_BUTTON_KEY = 'delete_route_button';
export const OPTIMIZE_ROUTE_BUTTON_KEY = 'optimize_route_button';

export const PANEL_BORDER_COLOR = `${Theme.Colors.gray4}`;
export const ROW_DIVIDER_BORDER_COLOR = `${Theme.Colors.gray2}`;

export const HEADER_HEIGHT = 73;

export const PANEL_BORDER_RADIUS = '0px';

export const LG_PANEL_BORDER_RADIUS = '24px';

export const DEFAULT_BORDER_RADIUS = '16px';

export const SCHEDULING_CONTENT_BACKGROUND = 'white';

export const UNSCHEDULED_COLOR_HEX = '#aaaaaa';

export const WORD_BREAK = 'word-break';

export const TRANSITION_DELAY_STANDARD_MILLI = '300ms';

export interface IWorkspaceFormFields {
  name: boolean;
}

export const METERS_TO_MILES_CONSTANT = 0.000621371;

export const REDIRECT_TO_MOBILE = 'routific://routes';

/**
 * Distance (in meters) beyond which an order completion (delivered/missed) is
 * flagged as happening too far from the planned location. Surfaced as a warning
 * affordance in the route (RDP) and order (ODP) detail panels.
 */
export const LOCATION_EXCEPTION_DISTANCE_THRESHOLD_METERS = 100;

export const MAX_ORGANIZATION_LOGO_SIZE_BYTES = 1 * 1024 * 1024;
